import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, FileSignature, Loader2, Plus, Search } from 'lucide-react'
import { api } from '../lib/api'
import { formatCents } from '../lib/money'
import {
  QuoteStatusLabel,
  type ClientListResponse,
  type QuoteListResponse,
  type QuoteStatusT,
} from '@toga/shared'

const PAGE_SIZE = 50

export const QUOTE_STATUS_BADGE: Record<QuoteStatusT, string> = {
  BORRADOR: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  ENVIADO: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  ACEPTADO: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  RECHAZADO: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  CADUCADO: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  FACTURADO: 'bg-toga-500/15 text-toga-300 border-toga-500/30',
}

export function QuoteStatusBadge({ status }: { status: QuoteStatusT }) {
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${QUOTE_STATUS_BADGE[status]}`}>
      {QuoteStatusLabel[status]}
    </span>
  )
}

export default function Quotes() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<QuoteListResponse | null>(null)
  const [clients, setClients] = useState<Array<{ id: string; displayName: string }>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState(searchParams.get('q') ?? '')

  const status = searchParams.get('status') ?? ''
  const clientId = searchParams.get('clientId') ?? ''
  const year = searchParams.get('year') ?? ''
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)

  useEffect(() => {
    api
      .get<ClientListResponse>('/clients?pageSize=100')
      .then((r) => setClients(r.items.map((c) => ({ id: c.id, displayName: c.displayName }))))
      .catch(() => setClients([]))
  }, [])

  // Debounce de la búsqueda hacia la URL.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (q.trim()) next.set('q', q.trim())
      else next.delete('q')
      next.delete('page')
      setSearchParams(next, { replace: true })
    }, 300)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      const qParam = searchParams.get('q')
      if (qParam) params.set('q', qParam)
      if (status) params.set('status', status)
      if (clientId) params.set('clientId', clientId)
      if (year) params.set('year', year)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      setData(await api.get<QuoteListResponse>(`/quotes?${params.toString()}`))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [searchParams, status, clientId, year, page])

  useEffect(() => {
    void load()
  }, [load])

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const years: number[] = []
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y)
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <FileSignature size={20} className="text-toga-300" />
            Presupuestos
          </h1>
          {data && (
            <p className="text-xs t-muted">
              {data.total} presupuesto{data.total !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/quotes/new')}
          className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-2"
        >
          <Plus size={15} /> Nuevo presupuesto
        </button>
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Número, concepto o cliente…"
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <Select
          label="Estado"
          value={status}
          onChange={(v) => setParam('status', v)}
          options={[
            { v: '', l: 'Todos' },
            ...Object.entries(QuoteStatusLabel).map(([k, l]) => ({ v: k, l })),
          ]}
        />
        <Select
          label="Cliente"
          value={clientId}
          onChange={(v) => setParam('clientId', v)}
          options={[{ v: '', l: 'Todos' }, ...clients.map((c) => ({ v: c.id, l: c.displayName }))]}
        />
        <Select
          label="Año"
          value={year}
          onChange={(v) => setParam('year', v)}
          options={[{ v: '', l: 'Todos' }, ...years.map((y) => ({ v: String(y), l: String(y) }))]}
        />
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/30 text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">Número</th>
              <th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-left">Concepto</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Validez</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> Cargando…
                </td>
              </tr>
            )}
            {!loading && data && data.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center">
                  <FileSignature size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    Sin presupuestos. Crea el primero con «Nuevo presupuesto».
                  </p>
                </td>
              </tr>
            )}
            {data?.items.map((it) => (
              <tr
                key={it.id}
                onClick={() => navigate(`/quotes/${it.id}`)}
                className="border-t border-base/40 hover:bg-hover cursor-pointer"
              >
                <td className="px-3 py-2 font-mono text-toga-300">{it.number}</td>
                <td className="px-3 py-2 t-primary">{it.clientDisplayName}</td>
                <td className="px-3 py-2 t-secondary truncate max-w-[260px]">
                  {it.title ?? <span className="t-faint">—</span>}
                  {it.caseCode && (
                    <span className="ml-2 text-[10px] font-mono t-faint">{it.caseCode}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <QuoteStatusBadge status={it.status} />
                </td>
                <td className="px-3 py-2 text-right font-mono t-primary">
                  {formatCents(it.totalCents)}
                </td>
                <td className="px-3 py-2 text-xs t-muted">
                  {new Date(it.issueDate).toLocaleDateString('es-ES')}
                </td>
                <td className="px-3 py-2 text-xs t-muted">
                  {it.validUntil ? new Date(it.validUntil).toLocaleDateString('es-ES') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs t-muted">
          <span>
            Página {data.page} de {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => setParam('page', String(data.page - 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={data.page >= totalPages}
              onClick={() => setParam('page', String(data.page + 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      <p className="text-xs t-faint">
        Los presupuestos no son documentos fiscales. Al aceptarse, conviértelos en factura
        borrador desde su ficha.
      </p>
    </div>
  )
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <label className="text-xs t-muted flex items-center gap-2">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  )
}
